import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  ArrowRight, Users, Clock, Target, Sparkles, 
  Brain, Lightbulb, Rocket, MapPin, TrendingUp, MessageCircle,
  GraduationCap, Award, Zap, Newspaper, ExternalLink, X, Gamepad2, Gift,
  Phone
} from "lucide-react";

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chefs-lieux de département
const FRENCH_CITIES = [
  "Paris", "Lyon", "Marseille", "Toulouse", "Bordeaux", "Lille", "Nantes", 
  "Strasbourg", "Rennes", "Nice", "Montpellier", "Grenoble", "Rouen", 
  "Toulon", "Dijon", "Angers", "Reims", "Le Havre", "Saint-Étienne",
  "Clermont-Ferrand", "Orléans", "Tours", "Metz", "Besançon", "Caen",
  "Amiens", "Limoges", "Perpignan", "Brest", "Pau", "Poitiers", "Troyes",
  "La Rochelle", "Valence", "Chambéry", "Ajaccio", "Bastia", "Colmar"
];

// Clients virtuels pour le télex
const VIRTUAL_CLIENTS = [
  "ETI industrielle de 250 salariés, Auvergne-Rhône-Alpes",
  "COMEX 15 membres, entreprise exportatrice 80% CA international",
  "CEO PME secteur de la Défense",
  "Master Class - Syndicat patronal, Nouvelle-Aquitaine",
  "Groupe bancaire régional, 120 collaborateurs formés",
  "Cabinet d'avocats d'affaires, Paris",
  "Scale-up Tech, 45 développeurs, Île-de-France",
  "Direction RH, Groupe hôtelier international",
  "COMEX 8 membres, Leader distribution alimentaire",
  "Startup HealthTech, Montpellier",
  "ETI familiale BTP, Grand Est",
  "Direction Financière, Groupe industriel coté",
  "Équipe Marketing, E-commerce mode, Lyon",
  "Cabinet conseil en stratégie, 30 consultants",
  "Directeur Général, PME agroalimentaire, Bretagne"
];

// Images androïdes IA générées par le client
const TRON_IMAGES = {
  hero: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/5c52dzth_1FF6B37C-6ED9-4724-AEBD-F1C8DDD70C73.png",
  guide: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/9ak2jpqm_1B94520E-3EBA-4CB1-9147-DE6B3C59B7DD.png",
  trainer: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/dr6znd2r_4641A994-51F7-4AEA-824E-5315348DDDF0.png",
  secondary: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/pnxjfnov_9EEA2BB1-33E3-4079-8844-3FDC343A1F3D.png",
  accent: "https://customer-assets.emergentagent.com/job_c4b8efe0-887c-4c49-ab73-3162491c4d73/artifacts/9ak2jpqm_1B94520E-3EBA-4CB1-9147-DE6B3C59B7DD.png"
};

const WHATSAPP_LINK = "https://wa.me/33644692901?text=Bonjour Lucie-IA ! Je souhaite en savoir plus sur les formations de l'Académie IA pour mon entreprise. Pouvez-vous me recontacter ?";

const HomePage = () => {
  const [currentCity, setCurrentCity] = useState(0);
  const [currentClient, setCurrentClient] = useState(0);
  const [trainedCount, setTrainedCount] = useState(1247);
  const [news, setNews] = useState([]);
  const [showGamePopup, setShowGamePopup] = useState(false);
  const [gamePopupDismissed, setGamePopupDismissed] = useState(false);
  
  const currentDate = new Date().toLocaleDateString('fr-FR', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Show game popup after 5 seconds (if not dismissed)
  useEffect(() => {
    const hasSeenPopup = sessionStorage.getItem('gamePopupSeen');
    if (!hasSeenPopup) {
      const timer = setTimeout(() => {
        setShowGamePopup(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  const dismissPopup = () => {
    setShowGamePopup(false);
    setGamePopupDismissed(true);
    sessionStorage.setItem('gamePopupSeen', 'true');
  };

  useEffect(() => {
    const fetchCounter = async () => {
      try {
        const response = await axios.get(`${API}/training-counter`);
        setTrainedCount(response.data.count);
      } catch (error) {
        console.error("Error fetching counter:", error);
      }
    };
    fetchCounter();
  }, []);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const response = await axios.get(`${API}/ai-news`);
        setNews(response.data.slice(0, 6));
      } catch (error) {
        console.error("Error fetching news:", error);
      }
    };
    fetchNews();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCity((prev) => (prev + 1) % FRENCH_CITIES.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentClient((prev) => (prev + 1) % VIRTUAL_CLIENTS.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Masterclass Groupe",
      description: "5 à 15 participants. Découvrez l'IA en équipe avec des ateliers pratiques.",
      duration: "Demi-journée ou journée",
      format: "masterclass_full"
    },
    {
      icon: <Target className="w-6 h-6" />,
      title: "Formation Individuelle",
      description: "2 heures personnalisées. Créez vos propres outils IA sur mesure.",
      duration: "2 heures / personne",
      format: "individual"
    },
    {
      icon: <Rocket className="w-6 h-6" />,
      title: "Ateliers Découverte",
      description: "Sessions ludiques avec jeux interactifs IA pour votre entreprise.",
      duration: "2 heures",
      format: "workshop_tour"
    }
  ];

  const stats = [
    { number: trainedCount.toLocaleString('fr-FR'), label: "Formés par l'Académie IA", live: true },
    { number: "98%", label: "Satisfaction" },
    { number: "50+", label: "Entreprises clientes" },
    { number: "100%", label: "Sur mesure" }
  ];

  const whyAcademie = [
    { icon: <GraduationCap className="w-6 h-6" />, title: "Experts certifiés", desc: "Formateurs passionnés et pédagogues" },
    { icon: <Award className="w-6 h-6" />, title: "N°1 en France", desc: "Leader de la formation IA entreprise" },
    { icon: <Zap className="w-6 h-6" />, title: "Résultats immédiats", desc: "Vos équipes opérationnelles dès J+1" },
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="home-page">
      <Navigation />
      
      {/* Game Popup */}
      <AnimatePresence>
        {showGamePopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={dismissPopup}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-w-md w-full glass-card p-6 border-[#007AFF]/30 bg-gradient-to-br from-[#0F0F10] to-[#1a1a2e]"
            >
              <button
                onClick={dismissPopup}
                className="absolute top-3 right-3 text-white/40 hover:text-white p-1"
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mx-auto mb-4">
                  <Gamepad2 className="w-8 h-8 text-white" />
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-2">
                  🎮 JEU GRATUIT !
                </h3>
                <p className="text-lg text-[#007AFF] font-semibold mb-3">
                  Développez votre première App IA en 30 secondes !
                </p>
                <p className="text-white/60 text-sm mb-6">
                  Découvrez concrètement ce que l'IA peut faire pour vous, à votre poste. 
                  <span className="text-green-400 font-medium"> + Bonus : -20% offerts !</span>
                </p>
                
                <Link
                  to="/jeu"
                  onClick={dismissPopup}
                  className="btn-primary w-full text-lg py-3 flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-5 h-5" />
                  Jouer maintenant !
                </Link>
                
                <button
                  onClick={dismissPopup}
                  className="mt-3 text-white/40 hover:text-white/60 text-sm"
                >
                  Non merci, plus tard
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Game Banner (visible if popup dismissed) */}
      {gamePopupDismissed && (
        <motion.div
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          className="fixed bottom-4 right-4 z-40 hidden md:block"
        >
          <Link
            to="/jeu"
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg shadow-green-500/30 hover:shadow-green-500/50 transition-shadow"
          >
            <Gamepad2 className="w-5 h-5" />
            <span className="font-semibold">Créez votre App IA !</span>
            <Gift className="w-4 h-4" />
          </Link>
        </motion.div>
      )}
      
      {/* Hero Section - Branding Académie IA */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
        <div className="hero-glow" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-20 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="relative z-10"
          >
            {/* Brand Badge */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-gradient-to-r from-[#007AFF]/20 to-cyan-500/20 border border-[#007AFF]/30 mb-6"
            >
              <div className="w-8 h-8 rounded-lg bg-[#007AFF] flex items-center justify-center">
                <Brain className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-semibold">
                <span className="text-white">ACADÉMIE</span>
                <span className="text-[#007AFF] ml-1">IA</span>
              </span>
              <span className="text-white/50 text-xs">• La référence formation IA</span>
            </motion.div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.1] tracking-tight mb-4 sm:mb-6">
              <span className="text-[#007AFF]">L'Académie</span> qui<br />
              démystifie<br />
              <span className="text-gradient">l'Intelligence</span><br />
              Artificielle
            </h1>
            
            <p className="text-base sm:text-lg text-white/60 leading-relaxed mb-6 sm:mb-8 max-w-lg">
              <strong className="text-white">Académie IA</strong> forme vos équipes à maîtriser l'IA. 
              Experts pédagogues, approche humaine, résultats concrets. 
              Partout en France.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link 
                to="/tunnel" 
                className="btn-primary flex items-center justify-center gap-2 text-sm sm:text-base"
                data-testid="hero-cta-primary"
              >
                Demander un devis
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </Link>
              <a 
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary flex items-center justify-center gap-2 text-sm sm:text-base border-green-500/50 text-green-400 hover:bg-green-500/20 hover:border-green-400 transition-all"
                data-testid="hero-whatsapp"
              >
                <WhatsAppIcon className="w-5 h-5" />
                <span className="font-semibold">Contactez Lucie-IA</span>
                <Phone className="w-3 h-3 animate-pulse" />
              </a>
            </div>

            {/* Quick stats */}
            <div className="flex items-center gap-4 sm:gap-8 mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-white/10">
              <div>
                <p className="text-xl sm:text-2xl font-bold text-white">1500€</p>
                <p className="text-xs sm:text-sm text-white/50">HT / jour</p>
              </div>
              <div className="w-px h-8 sm:h-10 bg-white/10" />
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-[#007AFF]" />
                <div>
                  <p className="text-xs sm:text-sm text-white/50">Académie IA se déplace</p>
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={currentCity}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="text-base sm:text-lg font-semibold text-[#007AFF]"
                    >
                      {FRENCH_CITIES[currentCity]}
                    </motion.p>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right - Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative hidden md:block"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent z-10" />
              <img 
                src={TRON_IMAGES.hero}
                alt="Académie IA - Votre guide formation"
                className="w-full h-[500px] lg:h-[600px] object-cover rounded-3xl"
                style={{ objectPosition: 'center top' }}
              />
              <div className="absolute inset-0 rounded-3xl" style={{
                background: 'radial-gradient(ellipse at center, rgba(0, 200, 255, 0.08) 0%, transparent 70%)',
                pointerEvents: 'none'
              }} />
              
              {/* Brand overlay */}
              <div className="absolute top-6 right-6 glass-card p-3 z-20">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-[#007AFF] flex items-center justify-center">
                    <Brain className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm">ACADÉMIE IA</p>
                    <p className="text-cyan-400 text-xs">Formation d'excellence</p>
                  </div>
                </div>
              </div>
              
              {/* Lucie-IA card */}
              <motion.div 
                className="absolute bottom-6 left-6 glass-card p-3 sm:p-4 z-20"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden border-2 border-green-500/50">
                    <img 
                      src={TRON_IMAGES.guide}
                      alt="Lucie-IA - Académie IA"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="text-white font-medium text-xs sm:text-sm">Lucie-IA</p>
                    <p className="text-green-400 text-xs">Votre contact Académie IA</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Mobile hero bg */}
        <div className="md:hidden absolute inset-0 opacity-20">
          <img src={TRON_IMAGES.hero} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent" />
        </div>

        <div className="absolute bottom-4 sm:bottom-8 left-4 sm:left-6 text-white/30 text-xs sm:text-sm z-10">
          Académie IA • {currentDate}
        </div>
      </section>

      {/* Télex */}
      <section className="py-3 sm:py-4 bg-[#007AFF]/10 border-y border-[#007AFF]/20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <span className="flex items-center gap-2 text-[#007AFF] font-semibold whitespace-nowrap text-xs sm:text-sm">
              <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              ACADÉMIE IA
            </span>
            <div className="flex-1 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.p
                  key={currentClient}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -50 }}
                  transition={{ duration: 0.5 }}
                  className="text-white/80 whitespace-nowrap text-xs sm:text-sm md:text-base truncate"
                >
                  Formation en cours : {VIRTUAL_CLIENTS[currentClient]}
                </motion.p>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* News Ticker - L'actu de l'IA */}
      {news.length > 0 && (
        <section className="py-6 sm:py-8 bg-gradient-to-r from-purple-900/20 via-[#0F0F10] to-purple-900/20 border-b border-purple-500/20" data-testid="news-ticker">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <Newspaper className="w-5 h-5 text-purple-400" />
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  Toute l'actu de <span className="text-purple-400">l'IA</span>
                </h3>
              </div>
              <Link 
                to="/espace-pedagogique" 
                className="ml-auto text-purple-400 hover:text-purple-300 text-sm flex items-center gap-1 transition-colors"
              >
                Voir tout
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            {/* Scrolling News */}
            <div className="relative overflow-hidden">
              <div className="flex gap-4 animate-scroll-news">
                {[...news, ...news].map((item, index) => (
                  <Link
                    key={`${item.id}-${index}`}
                    to={`/actualite/${item.id}`}
                    className="flex-shrink-0 w-[300px] sm:w-[350px] glass-card p-4 hover:border-purple-500/30 transition-all group"
                  >
                    <div className="flex items-start gap-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs flex-shrink-0 ${
                        item.badge === "TENDANCE" ? "bg-purple-500/20 text-purple-400" :
                        item.badge === "NOUVEAU" ? "bg-green-500/20 text-green-400" :
                        item.badge === "IMPORTANT" ? "bg-red-500/20 text-red-400" :
                        "bg-[#007AFF]/20 text-[#007AFF]"
                      }`}>
                        {item.badge}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium text-sm mb-1 group-hover:text-purple-400 transition-colors line-clamp-2">
                          {item.title}
                        </h4>
                        <p className="text-white/50 text-xs">{item.date}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Message différenciant */}
            <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20">
              <p className="text-white/80 text-sm text-center">
                <span className="text-amber-400 font-semibold">⚠️ Utiliser ChatGPT "un peu" n'est PAS utiliser l'IA.</span>
                {" "}L'Académie IA vous fait découvrir les <strong className="text-white">vraies</strong> possibilités de l'IA pour votre entreprise.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Why Académie IA */}
      <section className="py-12 sm:py-16 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
              Pourquoi choisir <span className="text-[#007AFF]">l'Académie IA</span> ?
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {whyAcademie.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass-card p-6 text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-[#007AFF]/10 flex items-center justify-center mx-auto mb-4 text-[#007AFF]">
                  {item.icon}
                </div>
                <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                <p className="text-white/60 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="stat-card"
                data-testid={`stat-${index}`}
              >
                <p className="stat-number text-2xl sm:text-4xl md:text-5xl flex items-center justify-center gap-2">
                  {stat.number}
                  {stat.live && (
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  )}
                </p>
                <p className="text-white/50 text-xs sm:text-sm mt-1 sm:mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-24 border-t border-white/5" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              Ce qu'ils disent de<br />
              <span className="text-[#007AFF]">l'Académie IA</span>
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto text-sm sm:text-base">
              Témoignages de professionnels formés par nos experts
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Testimonial 1 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-card p-6 relative"
              data-testid="testimonial-1"
            >
              <div className="absolute top-4 right-4 text-4xl text-[#007AFF]/20 font-serif">"</div>
              <p className="text-white/80 text-sm sm:text-base leading-relaxed mb-6 relative z-10">
                "En une journée, j'ai compris ce que je n'arrivais pas à saisir en 6 mois d'utilisation autodidacte. 
                Le formateur a su vulgariser des concepts complexes et surtout me montrer des cas concrets 
                applicables à mon métier de DAF."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#007AFF] to-cyan-500 flex items-center justify-center text-white font-bold text-sm">
                  SC
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Sophie C.</p>
                  <p className="text-white/50 text-xs">Directrice Administrative et Financière</p>
                  <p className="text-[#007AFF] text-xs">ETI industrielle, Lyon</p>
                </div>
              </div>
            </motion.div>

            {/* Testimonial 2 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="glass-card p-6 relative"
              data-testid="testimonial-2"
            >
              <div className="absolute top-4 right-4 text-4xl text-[#007AFF]/20 font-serif">"</div>
              <p className="text-white/80 text-sm sm:text-base leading-relaxed mb-6 relative z-10">
                "J'étais sceptique, voire réfractaire à l'IA. La formation m'a non seulement convaincu de son utilité, 
                mais m'a aussi permis de comprendre ses limites. Approche équilibrée, ni techno-béate ni alarmiste. 
                Exactement ce dont j'avais besoin."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold text-sm">
                  PD
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Philippe D.</p>
                  <p className="text-white/50 text-xs">PDG</p>
                  <p className="text-[#007AFF] text-xs">PME secteur Défense, 85 salariés</p>
                </div>
              </div>
            </motion.div>

            {/* Testimonial 3 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="glass-card p-6 relative"
              data-testid="testimonial-3"
            >
              <div className="absolute top-4 right-4 text-4xl text-[#007AFF]/20 font-serif">"</div>
              <p className="text-white/80 text-sm sm:text-base leading-relaxed mb-6 relative z-10">
                "On a formé 120 collaborateurs en 3 semaines grâce aux ateliers de découverte. Le format ludique 
                avec le jeu interactif a créé une vraie émulation. Certains sont devenus ambassadeurs IA dans leur service !"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-white font-bold text-sm">
                  ML
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Marie L.</p>
                  <p className="text-white/50 text-xs">DRH</p>
                  <p className="text-[#007AFF] text-xs">Groupe bancaire régional, Toulouse</p>
                </div>
              </div>
            </motion.div>

            {/* Testimonial 4 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="glass-card p-6 relative"
              data-testid="testimonial-4"
            >
              <div className="absolute top-4 right-4 text-4xl text-[#007AFF]/20 font-serif">"</div>
              <p className="text-white/80 text-sm sm:text-base leading-relaxed mb-6 relative z-10">
                "La masterclass a transformé notre COMEX. Nous avons enfin un langage commun pour parler d'IA 
                et surtout une vision partagée de ce qu'elle peut apporter à notre stratégie. 
                Investissement rentabilisé en moins d'un mois."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center text-white font-bold text-sm">
                  JB
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Jean-Baptiste M.</p>
                  <p className="text-white/50 text-xs">Directeur Général</p>
                  <p className="text-[#007AFF] text-xs">Scale-up Tech, Paris</p>
                </div>
              </div>
            </motion.div>

            {/* Testimonial 5 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="glass-card p-6 relative"
              data-testid="testimonial-5"
            >
              <div className="absolute top-4 right-4 text-4xl text-[#007AFF]/20 font-serif">"</div>
              <p className="text-white/80 text-sm sm:text-base leading-relaxed mb-6 relative z-10">
                "Ce qui m'a bluffé, c'est le volet philosophique de la formation. On ne nous a pas juste appris 
                à utiliser des outils, on nous a aidés à réfléchir à l'impact de l'IA sur nos métiers. 
                Rare et précieux."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm">
                  AB
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Aurélie B.</p>
                  <p className="text-white/50 text-xs">Associée</p>
                  <p className="text-[#007AFF] text-xs">Cabinet d'avocats d'affaires, Paris</p>
                </div>
              </div>
            </motion.div>

            {/* Testimonial 6 */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="glass-card p-6 relative"
              data-testid="testimonial-6"
            >
              <div className="absolute top-4 right-4 text-4xl text-[#007AFF]/20 font-serif">"</div>
              <p className="text-white/80 text-sm sm:text-base leading-relaxed mb-6 relative z-10">
                "J'ai créé mon premier assistant IA personnalisé pendant la formation individuelle. 
                Il me fait gagner 2 heures par jour sur mes analyses de marché. 
                Le formateur a parfaitement compris mes contraintes métier."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-bold text-sm">
                  TG
                </div>
                <div>
                  <p className="text-white font-medium text-sm">Thomas G.</p>
                  <p className="text-white/50 text-xs">Responsable Intelligence Économique</p>
                  <p className="text-[#007AFF] text-xs">ETI agroalimentaire, Bretagne</p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* CTA after testimonials */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-10 sm:mt-12"
          >
            <Link 
              to="/tunnel" 
              className="btn-primary inline-flex items-center gap-2"
              data-testid="testimonials-cta"
            >
              Rejoindre ces professionnels
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Game CTA Section */}
      <section className="py-16 sm:py-20 border-t border-white/5" data-testid="game-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-green-500/20 via-emerald-500/10 to-cyan-500/20 border border-green-500/30 p-8 sm:p-12"
          >
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-green-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-500/10 rounded-full blur-3xl" />
            
            <div className="relative grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/20 border border-green-500/30 mb-4">
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                  <span className="text-green-400 font-semibold text-sm">100% GRATUIT</span>
                </div>
                
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  🎮 JEU : Créez votre première <span className="text-green-400">App IA</span> en 30 secondes !
                </h2>
                
                <p className="text-white/70 text-lg mb-6">
                  Découvrez concrètement ce que l'Intelligence Artificielle peut faire 
                  <strong className="text-white"> pour VOUS, à VOTRE poste</strong>. 
                  Aucune connaissance technique requise !
                </p>
                
                <ul className="space-y-2 mb-8">
                  <li className="flex items-center gap-2 text-white/80">
                    <Sparkles className="w-5 h-5 text-green-400" />
                    Applications IA personnalisées selon votre métier
                  </li>
                  <li className="flex items-center gap-2 text-white/80">
                    <Gift className="w-5 h-5 text-green-400" />
                    <span className="text-green-400 font-semibold">Bonus : -20% offerts</span> sur votre formation
                  </li>
                </ul>
                
                <Link
                  to="/jeu"
                  className="btn-primary text-lg px-8 py-4 inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-400 hover:to-emerald-400 border-0"
                >
                  <Gamepad2 className="w-5 h-5" />
                  Jouer maintenant !
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
              
              <div className="hidden md:block relative">
                <img 
                  src={TRON_IMAGES.trainer}
                  alt="Jeu IA Académie"
                  className="w-full h-[350px] object-cover rounded-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent rounded-2xl" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
              Les formations<br />
              <span className="text-[#007AFF]">Académie IA</span>
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto text-sm sm:text-base">
              Trois formats pensés par l'Académie IA pour répondre à tous les besoins : 
              de l'initiation à l'expertise.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <Link
                  to={`/tunnel?format=${feature.format}`}
                  className="block feature-card glass-card-hover h-full cursor-pointer p-4 sm:p-6"
                  data-testid={`feature-card-${index}`}
                >
                  <div className="feature-icon w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">{feature.title}</h3>
                  <p className="text-white/60 mb-3 sm:mb-4 leading-relaxed text-sm">{feature.description}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[#007AFF] text-xs sm:text-sm">
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>{feature.duration}</span>
                    </div>
                    <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-white/40" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#007AFF]/5 to-transparent" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative order-2 md:order-1"
            >
              <img 
                src={TRON_IMAGES.trainer}
                alt="Formateurs Académie IA"
                className="w-full h-[300px] sm:h-[400px] md:h-[500px] object-cover rounded-3xl"
                style={{ objectPosition: 'center top' }}
              />
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              <div className="absolute inset-0 rounded-3xl" style={{
                boxShadow: 'inset 0 0 100px rgba(0, 200, 255, 0.1)',
                pointerEvents: 'none'
              }} />
              
              {/* Brand badge */}
              <div className="absolute top-4 right-4 glass-card px-3 py-1.5">
                <span className="text-white font-bold text-xs">ACADÉMIE</span>
                <span className="text-[#007AFF] font-bold text-xs ml-1">IA</span>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 md:order-2"
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                L'excellence<br />
                <span className="text-[#007AFF]">Académie IA</span>
              </h2>
              
              <div className="space-y-4 sm:space-y-6">
                <div className="flex gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#007AFF]/10 flex items-center justify-center flex-shrink-0">
                    <Brain className="w-4 h-4 sm:w-5 sm:h-5 text-[#007AFF]" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1 text-sm sm:text-base">Experts certifiés Académie IA</h4>
                    <p className="text-white/60 text-xs sm:text-sm">
                      Nos formateurs sont sélectionnés pour leur expertise ET leur pédagogie.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#007AFF]/10 flex items-center justify-center flex-shrink-0">
                    <Lightbulb className="w-4 h-4 sm:w-5 sm:h-5 text-[#007AFF]" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1 text-sm sm:text-base">Méthode Académie IA exclusive</h4>
                    <p className="text-white/60 text-xs sm:text-sm">
                      Notre approche unique : technique + philosophique + pratique.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 sm:gap-4">
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-[#007AFF]/10 flex items-center justify-center flex-shrink-0">
                    <Target className="w-4 h-4 sm:w-5 sm:h-5 text-[#007AFF]" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1 text-sm sm:text-base">Suivi post-formation Académie IA</h4>
                    <p className="text-white/60 text-xs sm:text-sm">
                      Accompagnement continu après la formation pour ancrer les acquis.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6 sm:mt-8">
                <Link 
                  to="/formations" 
                  className="btn-primary inline-flex items-center justify-center gap-2 text-sm"
                >
                  Découvrir les formations
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a 
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary inline-flex items-center justify-center gap-2 text-sm border-green-500/50 text-green-400 hover:bg-green-500/20 transition-all"
                >
                  <WhatsAppIcon className="w-5 h-5" />
                  <span className="font-semibold">Contactez Lucie-IA</span>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Risk Alert */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card p-6 sm:p-8 border-amber-500/20 bg-amber-500/5"
          >
            <div className="flex flex-col sm:flex-row items-start gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
                  L'IA transforme votre secteur - <span className="text-[#007AFF]">Académie IA</span> vous prépare
                </h3>
                <p className="text-white/70 mb-4 text-sm sm:text-base">
                  <strong className="text-amber-400">75% des entreprises du CAC40</strong> utilisent déjà l'IA. 
                  Ne subissez pas cette révolution : <strong>l'Académie IA</strong> vous aide à la dompter.
                </p>
                <Link
                  to="/tunnel"
                  className="inline-flex items-center gap-2 text-amber-400 font-medium text-sm hover:gap-3 transition-all"
                >
                  Évaluer mon exposition à l'IA
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quote */}
      <section className="py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="glass-card p-8 sm:p-12 text-center relative overflow-hidden"
          >
            {/* Brand watermark */}
            <div className="absolute top-4 right-4 opacity-20">
              <Brain className="w-16 h-16 text-[#007AFF]" />
            </div>
            
            <p className="text-xl sm:text-2xl md:text-3xl font-medium text-white leading-relaxed mb-6 sm:mb-8 font-accent relative z-10">
              "Avec <span className="text-[#007AFF]">l'Académie IA</span>,<br />
              l'Intelligence Artificielle<br />
              devient votre alliée."
            </p>
            <div className="flex items-center justify-center gap-3 relative z-10">
              <div className="w-10 h-10 rounded-lg bg-[#007AFF] flex items-center justify-center">
                <Brain className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-white font-semibold">ACADÉMIE IA</p>
                <p className="text-white/50 text-xs">La référence formation IA en France</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-[#007AFF]/10 via-transparent to-[#007AFF]/10" />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#007AFF] flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
              Rejoignez<br /><span className="text-[#007AFF]">l'Académie IA</span>
            </h2>
            <p className="text-white/60 mb-6 sm:mb-8 max-w-xl mx-auto text-sm sm:text-base">
              Demandez votre devis personnalisé. Lucie-IA, votre contact Académie IA, 
              vous répond sous 24h.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/tunnel" 
                className="btn-primary inline-flex items-center justify-center gap-2 text-base sm:text-lg px-6 sm:px-8 py-3 sm:py-4"
                data-testid="final-cta"
              >
                <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
                Obtenir mon devis
              </Link>
              <a 
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 px-6 py-3 rounded-full bg-green-500 text-white font-semibold hover:bg-green-600 hover:scale-105 transition-all shadow-lg shadow-green-500/30"
              >
                <WhatsAppIcon className="w-6 h-6" />
                <span>Contactez Lucie-IA</span>
                <Phone className="w-4 h-4 animate-pulse" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
